import { ThreeEvent } from '@react-three/fiber';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { createModelTreeForPorcessEditorAndSetUUID, ModelInModelTreeInProcessEditor } from '../../../utils/create-model-tree-for-porcess-editor-and-set-uuid/create-model-tree-for-porcess-editor-and-set-uuid';

type ModelProps = {
    id?: number;
    uuid: string;
    name: string;
    visible: boolean;
    position: THREE.Vector3Tuple;
    rotation: THREE.EulerTuple;
    scale: THREE.Vector3Tuple;
    model: string;
    onClick?: (event: ThreeEvent<MouseEvent>) => void;
    onDoubleClick?: (event: ThreeEvent<MouseEvent>) => void;
    onPointerMissed?: ((event: MouseEvent) => void);
    onModelLoaded?: (successfullyLoaded: boolean, model: ModelInModelTreeInProcessEditor) => void;
};

export const Model = ({
    id,
    uuid,
    name,
    visible,
    position,
    rotation,
    scale,
    model,
    onClick,
    onDoubleClick,
    onPointerMissed,
    onModelLoaded
}: ModelProps) => {
    const groupRef = useRef<THREE.Group<THREE.Object3DEventMap>>();
    const [loadedModel, setLoadedModel] = useState<THREE.Group<THREE.Object3DEventMap> | THREE.Object3D>();
    const ignoreUUIDsRef = useRef<string[]>();
    const [prevModel, setPrevModel] = useState<typeof model>();

    useEffect(() => {
        return () => {
            if (ignoreUUIDsRef.current) ignoreUUIDsRef.current.push(uuid);
        };
    }, [uuid]);

    if (model !== prevModel) {
        setPrevModel(model);

        if (!model) return null;

        const extension = model.split('.').pop()?.toLowerCase();
        
        switch (extension) {
            case '3dm': {
                const loadModel = async () => {
                    const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');
                    const loader = new Rhino3dmLoader();
                    loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                    loader.load(model, (object) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        if (onModelLoaded)
                            onModelLoaded(true, {
                                uuid,
                                visible,
                                name,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: object?.children ? createModelTreeForPorcessEditorAndSetUUID(object.children) : []
                            });

                        setLoadedModel(object);
                    }, undefined, (err) => {
                        if (onModelLoaded) {
                            onModelLoaded(false, {
                                uuid,
                                name,
                                visible,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: []
                            });
                        }
                    });
                };
                loadModel();
                break;
            }
            case 'glb':
            case 'gltf': {
                const loadModel = async () => {
                    const manager = new THREE.LoadingManager();

                    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                    const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                    const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                    const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                    const dracoLoader = new DRACOLoader();
                    dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                    const ktx2Loader = new KTX2Loader(manager);
                    ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                    const loader = new GLTFLoader(manager);
                    loader.setDRACOLoader(dracoLoader);
                    loader.setKTX2Loader(ktx2Loader);
                    loader.setMeshoptDecoder(MeshoptDecoder);

                    loader.load(model, (gltf) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        if (onModelLoaded)
                            onModelLoaded(true, {
                                uuid,
                                visible,
                                name,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: gltf.scene?.children ? createModelTreeForPorcessEditorAndSetUUID(gltf.scene.children) : []
                            });
                            
                        setLoadedModel(gltf.scene);
                    }, undefined, (err) => {
                        if (onModelLoaded) {
                            onModelLoaded(false, {
                                uuid,
                                name,
                                visible,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: []
                            });
                        }
                    });
                };
                loadModel();

                break;
            }
            case 'obj': {
                const loadModel = async () => {
                    const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');

                    const loader = new OBJLoader();
                    
                    loader.load(model, (object) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        if (onModelLoaded)
                            onModelLoaded(true, {
                                uuid,
                                visible,
                                name,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: object?.children ? createModelTreeForPorcessEditorAndSetUUID(object.children) : []
                            });

                        setLoadedModel(object);
                    }, undefined, (err) => {
                        if (onModelLoaded) {
                            onModelLoaded(false, {
                                uuid,
                                name,
                                visible,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: []
                            });
                        }
                    });
                };
                loadModel();
                break;
            }
            case 'drc': {
                const loadModel = async () => {
                    const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                    const loader = new DRACOLoader();
                    loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                    loader.load(model, (geometry) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        let object;

                        if ( geometry.index !== null ) {

                            const material = new THREE.MeshStandardMaterial();

                            object = new THREE.Mesh(geometry, material);
                            object.name = 'drcModell';

                        } else {

                            const material = new THREE.PointsMaterial({ size: 0.01 });
                            material.vertexColors = geometry.hasAttribute('color');

                            object = new THREE.Points(geometry, material);
                            object.name = 'drcModell';

                        }

                        if (onModelLoaded)
                            onModelLoaded(true, {
                                uuid,
                                visible,
                                name,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: object?.children ? createModelTreeForPorcessEditorAndSetUUID(object.children) : []
                            });

                        setLoadedModel(object);
                    }, undefined, (err) => {
                        if (onModelLoaded) {
                            onModelLoaded(false, {
                                uuid,
                                name,
                                visible,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: []
                            });
                        }
                    });
                };
                loadModel();
                
                break;
            }
            case 'fbx': {
                const loadModel = async () => {
                    const manager = new THREE.LoadingManager();
                    const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                    const loader = new FBXLoader(manager);

                    loader.load(model, (object) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        object.traverse(object => {
                            const _object: any = object;
                            if (_object.material) {
                                _object.material = new THREE.MeshStandardMaterial({
                                    color: _object.material.color
                                });
                            }
                        });

                        if (onModelLoaded)
                            onModelLoaded(true, {
                                uuid,
                                visible,
                                name,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: object?.children ? createModelTreeForPorcessEditorAndSetUUID(object.children) : []
                            });
                        
                        setLoadedModel(object);
                    }, undefined, (err) => {
                        if (onModelLoaded) {
                            onModelLoaded(false, {
                                uuid,
                                name,
                                visible,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: []
                            });
                        }
                    });
                };
                loadModel();

                break;
            }
            //case 'max':
            case '3ds': {
                const loadModel = async () => {
                    const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                    const loader = new TDSLoader();

                    loader.load(model, (object) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        if (onModelLoaded)
                            onModelLoaded(true, {
                                uuid,
                                visible,
                                name,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: object?.children ? createModelTreeForPorcessEditorAndSetUUID(object.children) : []
                            });

                        setLoadedModel(object);
                    }, undefined, (err) => {
                        if (onModelLoaded) {
                            onModelLoaded(false, {
                                uuid,
                                name,
                                visible,
                                isPartOfAModel: false,
                                position,
                                rotation,
                                scale,
                                children: []
                            });
                        }
                    });
                };
                loadModel();

                break;
            }
        }
    }

    return (
        <group
            uuid={uuid}
            name={name}
            visible={visible}
            ref={groupRef as React.Ref<THREE.Group<THREE.Object3DEventMap>>}
            position={position}
            rotation={rotation}
            scale={scale}
            onClick={onClick}
            onPointerMissed={onPointerMissed}
            onDoubleClick={onDoubleClick}
        >
            { loadedModel && <primitive object={loadedModel} /> }
        </group>
    );
};